import { parseQuery } from '@jjj/components';
import { ISettings } from '@dash/settings';
import { ROUTES } from '../containers/App/routes';
import appSettings, { fakeSignInData } from '../settings/index';
import { t, translateOptionLabel } from '../i18n';
import { UserRoles } from './userRoles';
import { LAST_VISITED_ROUTE_KEY, LOCAL_SETTINGS_KEY } from './localStorageKeys.const';

export function getSettings() {
  const localStorageSettings = localStorage.getItem(LOCAL_SETTINGS_KEY);
  const settings = localStorageSettings
    ? JSON.parse(localStorageSettings)
    : {
        apiUrl: appSettings.apiUrl,
        wsUrl: appSettings.wsUrl,
        organizationCode: appSettings.organizationCode,
        email: fakeSignInData.profileObj.email,
      };

  if (!settings.apiUrl) {
    settings.apiUrl = appSettings.apiUrl;
  }

  if (!settings.wsUrl) {
    settings.wsUrl = appSettings.wsUrl;
  }

  if (!settings.organizationCode) {
    settings.organizationCode = appSettings.organizationCode;
  }

  return settings;
}

export function updateSettings(settings: Partial<ISettings>): void {
  const cachedSettings = getSettings();
  localStorage.setItem(LOCAL_SETTINGS_KEY, JSON.stringify({ ...cachedSettings, ...settings }));
}

export function updateLastVisitedRoute(route: string | null) {
  localStorage.setItem(LAST_VISITED_ROUTE_KEY, route || '');
}

export function clearLastVisitedRoute() {
  localStorage.removeItem(LAST_VISITED_ROUTE_KEY);
}

export function getDefaultInitialRoute(role: string) {
  const root = '/';
  switch (role) {
    case UserRoles.RECRUITER:
      return root + ROUTES.DASHBOARD;
    case UserRoles.PROCUREMENT:
      return root + ROUTES.AGENCIES_ACTIVE_LIST;
    default:
      return `${root}jobs?tab=active`;
  }
}

export function getHomePageForRole(role: string) {
  const root = '/';

  switch (role) {
    case UserRoles.PROCUREMENT:
      return {
        link: root + ROUTES.AGENCIES_ACTIVE_LIST,
        title: t('dash.agencies.active.header'),
      };
    default:
      return {
        link: root + ROUTES.DASHBOARD,
        title: t('dash.notFoundPage.go_to_dashboard_label'),
      };
  }
}

export function getLastVisitedRoute(role: string) {
  const lastRoute = (localStorage.getItem(LAST_VISITED_ROUTE_KEY) || '').replace(/^"|"$/g, '');

  return lastRoute || getDefaultInitialRoute(role);
}

export function groupArrayByField(inputArray: [], field: string) {
  const result = inputArray.reduce((resultingObject, arrayItem) => {
    resultingObject[arrayItem[field]] = resultingObject[arrayItem[field]] || [];
    resultingObject[arrayItem[field]].push(arrayItem);

    return resultingObject;
  }, Object.create(null));

  return result;
}

export const getPercentage = (assigned: number, required: number) => {
  if (required === 0) {
    return null;
  }

  return ((assigned / required) * 100).toFixed(0);
};

export const maskOut = (
  valueToMask: string,
  maskCharacters: string,
  visibleCharactersCount: number,
  maskOutFirst = true,
) => {
  const pattern = maskOutFirst ? `^.*(?=.{${visibleCharactersCount}}$)` : `(?<=.{${visibleCharactersCount}})(.*)$`;
  const regex = new RegExp(pattern);

  return valueToMask && valueToMask.replace(regex, maskCharacters);
};

export const getDaysOfWeekList = () => {
  return ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(name => ({
    id: name,
    name: translateOptionLabel('day_of_the_week', name.toLocaleLowerCase(), name),
  }));
};

interface HistoryLocation {
  search: string;
}

interface History {
  location: HistoryLocation;
}

export function onCollapseGetTabs(tab: string, tabs: string | string[], history: History, key = 'tab'): string[] {
  const parsedQuery = parseQuery(history.location.search);
  let newTabs = !tabs.length ? parsedQuery[key] || [] : tabs;

  if (typeof newTabs === 'string') {
    newTabs = newTabs.split(',');
  }

  return newTabs.includes(tab) ? newTabs.filter((item: string) => item !== tab) : [...newTabs, tab];
}

export const isTabOpen = (search: string, currentTab: string, key = 'tab') => {
  const { [key]: tabs } = parseQuery(search);

  if (Array.isArray(tabs)) {
    return tabs.find(tab => tab === currentTab);
  }

  if (tabs === currentTab) {
    return currentTab;
  }
};
